import { useRef } from 'react';
import type { Message } from '@shared/types';
import { formatRelative } from '../../lib/time';
import { useMagneticHover } from '../../lib/useMagneticHover';
import { CagnotteBadge } from './CagnotteBadge';

export function MessageCard({ message, now }: { message: Message; now?: number }) {
  const hasGift = !!message.giftUrl;
  const cardRef = useRef<HTMLElement>(null);
  // Magnetic hover : la card s'attire vers le curseur dans un rayon 140px
  // avec une force 0.18 (subtile, ne déforme pas la lecture du texte).
  // Désactivé si l'utilisateur a `prefers-reduced-motion` actif (a11y).
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const magneticStyle = useMagneticHover(cardRef, {
    radius: 140,
    strength: 0.18,
    lerp: 0.16,
    disabled: prefersReducedMotion,
  });
  return (
    <article
      ref={cardRef}
      className={`chat-card${hasGift ? ' chat-card--gift' : ''}`}
      style={magneticStyle}
      data-testid="chat-card"
      data-has-gift={hasGift ? 'true' : 'false'}
    >
      <header className="chat-card__head">
        <h3 className="chat-card__title">{message.title}</h3>
        <time
          className="chat-card__time"
          dateTime={new Date(message.createdAt).toISOString()}
        >
          {formatRelative(message.createdAt, now)}
        </time>
      </header>
      <p className="chat-card__body">{message.body}</p>
      {hasGift && message.giftUrl && (
        <div className="chat-cagnotte" data-testid="chat-cagnotte">
          <CagnotteBadge url={message.giftUrl} label={message.giftLabel} />
          <span className="chat-cagnotte__label">{message.giftLabel ?? 'cagnotte'}</span>
          <a
            className="chat-cagnotte__link"
            href={message.giftUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            → ouvrir
          </a>
        </div>
      )}
    </article>
  );
}
